import React, { useEffect, useState, useMemo, useCallback } from "react";
import Container from "../../Components/Container";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

const readJson = async (res) => {
  if (!res.ok) throw new Error("Sunucu yanıt vermedi");
  return res.json();
};

const isListError = (data) =>
  data != null &&
  typeof data === "object" &&
  !Array.isArray(data) &&
  data.status === 400;

const asRowArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data != null && Array.isArray(data.data)) return data.data;
  return [];
};

const fmtMoney = (n) =>
  `${Number(n == null || Number.isNaN(Number(n)) ? 0 : Number(n)).toFixed(2)} TL`;

const sumLinePrice = (rows) =>
  rows.reduce(
    (acc, item) =>
      acc + Number(item.price || 0) * Number(item.count || 0),
    0
  );

const sumLineCost = (rows) =>
  rows.reduce(
    (acc, item) =>
      acc + Number(item.cost || 0) * Number(item.count || 0),
    0
  );

/** Tailwind: tam sınıf adları (dinamik string kullanma) */
const TONE = {
  indigo: {
    card: "rounded-xl border border-indigo-100 bg-indigo-50 p-5",
    value: "text-2xl font-bold text-indigo-600",
    sub: "mt-2 text-base text-indigo-600",
    title: "mb-3 text-base font-medium text-gray-700",
    txTitle: "mb-4 text-lg font-medium text-indigo-900",
  },
  green: {
    card: "rounded-xl border border-green-100 bg-green-50 p-5",
    value: "text-2xl font-bold text-green-600",
    sub: "mt-2 text-base text-green-600",
    title: "mb-3 text-base font-medium text-gray-700",
    txTitle: "mb-4 text-lg font-medium text-green-900",
  },
  blue: {
    card: "rounded-xl border border-blue-100 bg-blue-50 p-5",
    value: "text-2xl font-bold text-blue-600",
    sub: "mt-2 text-base text-blue-600",
    title: "mb-3 text-base font-medium text-gray-700",
    txTitle: "mb-4 text-lg font-medium text-blue-900",
  },
  purple: {
    card: "rounded-xl border border-purple-100 bg-purple-50 p-5",
    value: "text-2xl font-bold text-purple-600",
    sub: "mt-2 text-base text-purple-600",
    title: "mb-3 text-base font-medium text-gray-700",
    txTitle: "mb-4 text-lg font-medium text-purple-900",
  },
  yellow: {
    card: "rounded-xl border border-amber-100 bg-amber-50 p-5",
    value: "text-2xl font-bold text-amber-700",
    sub: "mt-2 text-base text-amber-700",
    title: "mb-3 text-base font-medium text-gray-700",
    txTitle: "mb-4 text-lg font-medium text-amber-900",
  },
  red: {
    card: "rounded-xl border border-red-100 bg-red-50 p-5",
    value: "text-2xl font-bold text-red-600",
    sub: "mt-2 text-base text-red-600",
    title: "mb-3 text-base font-medium text-gray-700",
    txTitle: "mb-4 text-lg font-medium text-red-900",
  },
};

const StatCard = ({ title, value, subValue, toneKey }) => {
  const t = TONE[toneKey] || TONE.indigo;
  return (
    <div className={t.card}>
      <h3 className={t.title}>{title}</h3>
      <p className={t.value}>{value}</p>
      {subValue != null && subValue !== "" && (
        <p className={t.sub}>{subValue}</p>
      )}
    </div>
  );
};

const TransactionCard = ({ title, count, amount, toneKey }) => {
  const t = TONE[toneKey] || TONE.indigo;
  return (
    <div className={t.card}>
      <h3 className={t.txTitle}>{title}</h3>
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-base text-gray-600">Adet:</span>
          <span className="text-base font-medium">{count}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-base text-gray-600">Tutar:</span>
          <span className="text-base font-medium">{amount} TL</span>
        </div>
      </div>
    </div>
  );
};

const Report = () => {
  const [day, setDay] = useState(false);
  const [days, setDays] = useState([]);
  const [data, setData] = useState([]);
  const [cat, setCat] = useState([]);
  const [selectedCat, setSelectedCat] = useState("");
  const [storeList, setStoreList] = useState([]);
  const [selectedStore, setSelectedStore] = useState("0");
  const [price, setPrice] = useState(0);
  const [maliyet, setMaliyet] = useState(0);
  const [selectedType, setSelectedType] = useState("-1");
  const [selectedMount, setSelectedMount] = useState(1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [salesCount, setSalesCount] = useState(0);
  const [returnCount, setReturnCount] = useState(0);
  const [zCount, setZCount] = useState(0);
  const [costs, setCosts] = useState([]);
  const navigate = useNavigate();

  const [totalReturnAmount, setTotalReturnAmount] = useState(0);
  const [totalZAmount, setTotalZAmount] = useState(0);
  const [totalG, setTotalG] = useState(0);

  const [detailSaleCount, setDetailSaleCount] = useState(0);
  const [detailReturnCount, setDetailReturnCount] = useState(0);
  const [detailZCount, setDetailZCount] = useState(0);
  const [detailSaleAmount, setDetailSaleAmount] = useState(0);
  const [detailReturnAmount, setDetailReturnAmount] = useState(0);
  const [detailZAmount, setDetailZAmount] = useState(0);

  const years = useMemo(() => {
    const startYear = 1900;
    const endYear = new Date().getFullYear();
    const y = [];
    for (let year = startYear; year <= endYear; year++) y.push(year);
    return y;
  }, []);

  const groupSalesData = useCallback((raw) => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const groupedData = raw.reduce((acc, sale) => {
      const { name, count, price, date, time, user, category, id, cost } =
        sale;
      if (!acc[name]) {
        acc[name] = {
          name,
          totalQuantity: 0,
          totalPrice: 0,
          date,
          time,
          user,
          category,
          id,
          price,
          cost,
        };
      }
      acc[name].totalQuantity += parseInt(count, 10) || 0;
      acc[name].totalPrice +=
        (parseInt(price, 10) || 0) * (parseInt(count, 10) || 0);
      return acc;
    }, {});

    return Object.values(groupedData);
  }, []);

  const applyRowAggregates = useCallback((rows) => {
    const saleRows = rows.filter((item) => Number(item.type) === 1);
    const retRows = rows.filter((item) => Number(item.type) === 0);
    const zRows = rows.filter((item) => Number(item.type) === 2);

    setPrice(sumLinePrice(rows));
    setMaliyet(sumLineCost(rows));
    setSalesCount(saleRows.length);
    setReturnCount(retRows.length);
    setZCount(zRows.length);
    setTotalReturnAmount(sumLinePrice(retRows));
    setTotalZAmount(sumLinePrice(zRows));
  }, []);

  const handleChange = (event) => {
    setSelectedYear(Number(event.target.value));
  };

  const types = [
    { name: "Satış", value: 1 },
    { name: "İade", value: 0 },
    { name: "Zayiyat", value: 2 },
  ];

  const mounts = [
    { name: "Ocak", value: 1 },
    { name: "Şubat", value: 2 },
    { name: "Mart", value: 3 },
    { name: "Nisan", value: 4 },
    { name: "Mayıs", value: 5 },
    { name: "Haziran", value: 6 },
    { name: "Temmuz", value: 7 },
    { name: "Ağustos", value: 8 },
    { name: "Eylül", value: 9 },
    { name: "Ekim", value: 10 },
    { name: "Kasım", value: 11 },
    { name: "Aralık", value: 12 },
  ];

  useEffect(() => {
    const selectCat = new FormData();
    selectCat.append("action", "select");
    fetch(process.env.REACT_APP_BASE_URL + "categories.php", {
      method: "POST",
      body: selectCat,
    })
      .then(readJson)
      .then((d) => {
        if (isListError(d)) {
          toast.error(d.message || "Kategoriler alınamadı");
          setCat([]);
          return;
        }
        setCat(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        toast.error("Kategoriler yüklenemedi");
        setCat([]);
      });

    const selectStore = new FormData();
    selectStore.append("action", "select");
    fetch(process.env.REACT_APP_BASE_URL + "store.php", {
      method: "POST",
      body: selectStore,
    })
      .then(readJson)
      .then((d) => {
        if (isListError(d)) {
          toast.error(d.message || "Depolar alınamadı");
          setStoreList([]);
          return;
        }
        setStoreList(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        toast.error("Depolar yüklenemedi");
        setStoreList([]);
      });
  }, []);

  const columns = useMemo(
    () => [
      { name: "No", selector: (row) => row.id, sortable: true },
      { name: "Kategori", selector: (row) => row.category, sortable: true },
      { name: "İsim", selector: (row) => row.name, sortable: true },
      { name: "Tarih", selector: (row) => row.date, sortable: true },
      { name: "Fiyat", selector: (row) => row.price, sortable: true },
      { name: "Maliyet", selector: (row) => row.cost, sortable: true },
    ],
    []
  );

  const tableStyles = useMemo(
    () => ({
      table: {
        style: {
          backgroundColor: "white",
          borderRadius: "0.5rem",
        },
      },
      headRow: {
        style: {
          backgroundColor: "#f8fafc",
          borderBottomWidth: "1px",
          borderBottomColor: "#e2e8f0",
        },
      },
      headCells: {
        style: {
          color: "#64748b",
          fontSize: "0.875rem",
          fontWeight: "600",
          padding: "1rem",
        },
      },
      cells: {
        style: {
          fontSize: "0.875rem",
          padding: "1rem",
          color: "#4b5563",
        },
      },
    }),
    []
  );

  const selectMoundfunc = (_e, value) => {
    setSelectedMount(value);
    setDay(false);

    const mountForm = new FormData();
    mountForm.append("action", "selectByMount");
    mountForm.append("mount", value);
    mountForm.append("date", String(selectedYear));

    fetch(process.env.REACT_APP_BASE_URL + "report.php", {
      method: "POST",
      body: mountForm,
    })
      .then(readJson)
      .then((payload) => {
        if (isListError(payload)) {
          toast.error(payload.message || "Rapor alınamadı");
          setData([]);
          applyRowAggregates([]);
          return;
        }
        const rows = asRowArray(payload);
        setData(rows);
        applyRowAggregates(rows);
      })
      .catch(() => {
        toast.error("Rapor yüklenirken hata oluştu");
        setData([]);
        applyRowAggregates([]);
      });

    const selectCost = new FormData();
    selectCost.append("action", "selectCost");
    selectCost.append("date", String(selectedYear));
    selectCost.append("mount", String(value));
    fetch(process.env.REACT_APP_BASE_URL + "panel.php", {
      method: "POST",
      body: selectCost,
    })
      .then(readJson)
      .then((payload) => {
        if (isListError(payload)) {
          setCosts([]);
          setTotalG(0);
          return;
        }
        const costRows = Array.isArray(payload) ? payload : [];
        setCosts(costRows);
        setTotalG(
          costRows.reduce((acc, item) => acc + Number(item.amount || 0), 0)
        );
      })
      .catch(() => {
        setCosts([]);
        setTotalG(0);
      });

    const selectDailyReport = new FormData();
    selectDailyReport.append("action", "days");
    selectDailyReport.append("mount", String(value));
    selectDailyReport.append("date", String(selectedYear));
    fetch(process.env.REACT_APP_BASE_URL + "report.php", {
      method: "POST",
      body: selectDailyReport,
    })
      .then(readJson)
      .then((payload) => {
        if (isListError(payload)) {
          setDays([]);
          return;
        }
        setDays(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setDays([]));
  };

  const detailSearch = () => {
    const formdata = new FormData();
    formdata.append("action", "detailSearch");
    formdata.append("mount", String(selectedMount));
    formdata.append("date", String(selectedYear));
    formdata.append("categories", selectedCat);
    formdata.append("store", selectedStore);
    formdata.append("type", selectedType);

    fetch(process.env.REACT_APP_BASE_URL + "report.php", {
      method: "POST",
      body: formdata,
    })
      .then(readJson)
      .then((payload) => {
        if (isListError(payload)) {
          toast.error(payload.message || "Arama başarısız");
          setData([]);
          setDetailSaleCount(0);
          setDetailReturnCount(0);
          setDetailZCount(0);
          setDetailSaleAmount(0);
          setDetailReturnAmount(0);
          setDetailZAmount(0);
          return;
        }
        const rows = asRowArray(payload);
        setData(rows);

        const saleRows = rows.filter((item) => Number(item.type) === 1);
        const retRows = rows.filter((item) => Number(item.type) === 0);
        const zRows = rows.filter((item) => Number(item.type) === 2);

        setDetailSaleCount(saleRows.length);
        setDetailReturnCount(retRows.length);
        setDetailZCount(zRows.length);
        setDetailSaleAmount(sumLinePrice(saleRows));
        setDetailReturnAmount(sumLinePrice(retRows));
        setDetailZAmount(sumLinePrice(zRows));
      })
      .catch(() => {
        toast.error("Arama sırasında hata oluştu");
        setDetailSaleCount(0);
        setDetailReturnCount(0);
        setDetailZCount(0);
        setDetailSaleAmount(0);
        setDetailReturnAmount(0);
        setDetailZAmount(0);
      });
  };

  const netKazanc = price - maliyet - totalReturnAmount - totalZAmount - totalG;

  const reportHandel = () => {
    navigate("/dashboard/reportPage", {
      state: {
        calc: [
          {
            Satış: price,
            Maliyet: maliyet,
            Kar: price - maliyet,
            Gider: totalG,
            "Net kazanç": netKazanc,
          },
        ],
        counts: [
          {
            Satış: {
              Tutar: Number(price).toFixed(2),
              Adet: salesCount,
            },
            İade: {
              Tutar: Number(totalReturnAmount).toFixed(2),
              Adet: returnCount,
            },
            Zaiat: {
              Tutar: Number(totalZAmount).toFixed(2),
              Adet: zCount,
            },
          },
        ],
        basket: groupSalesData(data),
        date: {
          date:
            day !== false && day !== ""
              ? day
              : `0.${selectedMount}.${selectedYear}`,
        },
        costs: costs,
      },
    });
  };

  const getDayDetails = (dayVal) => {
    setDay(dayVal);
    const formdata = new FormData();
    formdata.append("action", "selectDayDetails");
    formdata.append("date", dayVal);

    fetch(process.env.REACT_APP_BASE_URL + "report.php", {
      method: "POST",
      body: formdata,
    })
      .then(readJson)
      .then((payload) => {
        if (isListError(payload)) {
          toast.error(payload.message || "Gün detayı alınamadı");
          setData([]);
          applyRowAggregates([]);
          return;
        }
        const raw = asRowArray(payload);
        setData(groupSalesData(raw));
        applyRowAggregates(raw);
      })
      .catch(() => {
        toast.error("Gün detayı yüklenemedi");
        setData([]);
        applyRowAggregates([]);
      });

    const selectCost = new FormData();
    selectCost.append("action", "selectCostDaily");
    selectCost.append("date", dayVal);
    fetch(process.env.REACT_APP_BASE_URL + "panel.php", {
      method: "POST",
      body: selectCost,
    })
      .then(readJson)
      .then((payload) => {
        if (isListError(payload)) {
          setCosts([]);
          setTotalG(0);
          return;
        }
        const costRows = Array.isArray(payload) ? payload : [];
        setCosts(costRows);
        setTotalG(
          costRows.reduce((acc, item) => acc + Number(item.amount || 0), 0)
        );
      })
      .catch(() => {
        setCosts([]);
        setTotalG(0);
      });
  };

  const storeSelected = selectedStore !== "0" && selectedStore !== 0;

  const detailHasResults =
    detailSaleCount > 0 ||
    detailReturnCount > 0 ||
    detailZCount > 0;

  return (
    <Container padding bg="#f8fafc">
      <Toaster
        position="top-right"
        toastOptions={{ className: "text-sm font-medium shadow-lg" }}
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-4 px-4 pb-12 pt-2 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex w-full flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Satış raporları
            </h1>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <select
                value={selectedYear}
                onChange={handleChange}
                className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-2.5 text-sm font-medium text-slate-900 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={reportHandel}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-emerald-700 sm:flex-initial"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Rapor oluştur
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/80 bg-white py-4 shadow-sm">
          <div className="flex flex-nowrap items-center justify-start gap-2 overflow-x-auto px-3 sm:px-4">
            {mounts.map((item) => (
              <button
                type="button"
                key={item.value}
                onClick={(e) => selectMoundfunc(e, item.value)}
                className={`min-w-[100px] rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                  selectedMount === item.value
                    ? "bg-indigo-600 text-white shadow-md"
                    : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>

        {days.length > 0 && (
          <div className="hidden w-full rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm md:block">
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 lg:gap-3">
              {days.map((item, index) => {
                const dayValue = Object.values(item)[0];
                return (
                  <button
                    type="button"
                    key={index}
                    onClick={() => getDayDetails(String(dayValue))}
                    className={`flex items-center justify-center rounded-xl p-3 text-sm font-medium transition-colors lg:text-base ${
                      day === String(dayValue)
                        ? "bg-indigo-600 text-white shadow-md"
                        : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {dayValue}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="w-full rounded-2xl border border-slate-200/80 bg-white shadow-sm md:hidden">
          <details className="group w-full">
            <summary className="flex cursor-pointer list-none items-center justify-between p-4">
              <h2 className="font-semibold text-slate-800">Gün raporu</h2>
            </summary>
            <div className="grid grid-cols-3 gap-3 p-4 md:hidden">
              {days.map((item, index) => {
                const dayValue = Object.values(item)[0];
                return (
                  <button
                    type="button"
                    key={index}
                    onClick={() => getDayDetails(String(dayValue))}
                    className={`flex items-center justify-center rounded-xl p-3 text-sm font-medium transition-colors ${
                      day === String(dayValue)
                        ? "bg-indigo-600 text-white shadow-md"
                        : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {dayValue}
                  </button>
                );
              })}
            </div>
          </details>
        </div>

        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Toplam satış" value={fmtMoney(price)} toneKey="indigo" />
          <StatCard
            title="Toplam kâr"
            value={fmtMoney(price - maliyet)}
            toneKey="green"
          />
          <StatCard title="Toplam maliyet" value={fmtMoney(maliyet)} toneKey="blue" />
          <StatCard title="Net kazanç" value={fmtMoney(netKazanc)} toneKey="purple" />
        </div>

        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          <TransactionCard
            title="Satışlar"
            count={salesCount}
            amount={Number(price).toFixed(2)}
            toneKey="green"
          />
          <TransactionCard
            title="İadeler"
            count={returnCount}
            amount={Number(totalReturnAmount).toFixed(2)}
            toneKey="yellow"
          />
          <TransactionCard
            title="Zayiat"
            count={zCount}
            amount={Number(totalZAmount).toFixed(2)}
            toneKey="red"
          />
        </div>

        {costs.length > 0 && (
          <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h2 className="text-lg font-semibold text-slate-800">Giderler</h2>
              <span className="text-sm font-semibold text-slate-600">
                Toplam: {fmtMoney(totalG)}
              </span>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
                      Sno
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
                      Tür
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-slate-500">
                      Tutar
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
                      Tarih
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500">
                      Açıklama
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {costs.map((item, index) => (
                    <tr key={index} className="hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">
                        {Object.values(item)[0]}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {Object.values(item)[1]}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-slate-600">
                        {Object.values(item)[2]} TL
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">
                        {Object.values(item)[3]}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">
                        {Object.values(item)[4]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mb-4 rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <details className="group w-full">
            <summary className="flex cursor-pointer list-none items-center justify-between p-4">
              <h2 className="font-semibold text-slate-800">Detaylı arama</h2>
              <svg
                className="h-5 w-5 text-slate-500 transition-transform group-open:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </summary>
            <div className="border-t border-slate-200 p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Depo
                  </label>
                  <select
                    value={selectedStore}
                    onChange={(e) => setSelectedStore(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  >
                    <option value="0">Depo seçiniz</option>
                    {storeList.map((s) => (
                      <option key={s.id ?? s.name} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {storeSelected && (
                  <>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Kategori
                      </label>
                      <select
                        value={selectedCat}
                        onChange={(e) => setSelectedCat(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      >
                        <option value="">Kategori seçiniz</option>
                        {cat.map((category) => (
                          <option
                            key={category.id ?? category.name}
                            value={category.name}
                          >
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Tür
                      </label>
                      <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      >
                        <option value="-1">Tür seçiniz</option>
                        {types.map((item) => (
                          <option key={item.value} value={String(item.value)}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>

              {storeSelected && (
                <button
                  type="button"
                  onClick={detailSearch}
                  className="mt-4 w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 sm:w-auto"
                >
                  Ara
                </button>
              )}

              {detailHasResults && (
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <StatCard
                    title="Satış tutarı"
                    value={fmtMoney(detailSaleAmount)}
                    subValue={`${detailSaleCount} adet`}
                    toneKey="green"
                  />
                  <StatCard
                    title="İade tutarı"
                    value={fmtMoney(detailReturnAmount)}
                    subValue={`${detailReturnCount} adet`}
                    toneKey="yellow"
                  />
                  <StatCard
                    title="Zayiat tutarı"
                    value={fmtMoney(detailZAmount)}
                    subValue={`${detailZCount} adet`}
                    toneKey="red"
                  />
                </div>
              )}
            </div>
          </details>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <DataTable
            columns={columns}
            data={groupSalesData(data)}
            pagination
            customStyles={tableStyles}
            noDataComponent={
              <div className="py-12 text-center text-sm text-slate-500">
                Kayıt yok. Ay seçimi veya arama yapın.
              </div>
            }
          />
        </div>
      </div>
    </Container>
  );
};

export default Report;
